import React from "react";

// Chunky hand-drawn doodle set - inline SVG, no external deps
// Uses currentColor for stroke so parents can theme them.

const strokeProps = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 3.5,
  strokeLinecap: "round",
  strokeLinejoin: "round",
};

export const DoodleBeaker = ({ className = "w-40 h-40 text-black" }) => (
  <svg viewBox="0 0 200 200" className={className}>
    <g {...strokeProps}>
      <path d="M75 30 L75 80 L45 145 Q42 168 60 172 L140 172 Q158 168 155 145 L125 80 L125 30" />
      <path d="M65 30 L135 30" />
      <path d="M55 130 L145 130" />
      {/* bubbles */}
      <circle cx="85" cy="150" r="6" fill="#FFB703" stroke="none" />
      <circle cx="110" cy="140" r="4" fill="#FF4D00" stroke="none" />
      <circle cx="120" cy="158" r="5" fill="#2A64F6" stroke="none" />
      {/* sparkles */}
      <path d="M40 50 L45 55 M40 55 L45 50" />
      <path d="M170 90 L175 95 M170 95 L175 90" />
      <path d="M155 45 L160 50 M155 50 L160 45" />
    </g>
  </svg>
);

export const DoodleStar = ({ className = "w-8 h-8 text-black" }) => (
  <svg viewBox="0 0 100 100" className={className}>
    <path
      d="M50 8 L58 38 L88 42 L64 62 L72 92 L50 76 L28 92 L36 62 L12 42 L42 38 Z"
      {...strokeProps}
      fill="currentColor"
    />
  </svg>
);

export const DoodleSparkle = ({ className = "w-6 h-6 text-black" }) => (
  <svg viewBox="0 0 60 60" className={className}>
    <path
      d="M30 4 L34 26 L56 30 L34 34 L30 56 L26 34 L4 30 L26 26 Z"
      fill="currentColor"
    />
  </svg>
);

// Rising chart with dots + character
export const DoodleChart = ({ className = "w-56 h-56 text-black" }) => (
  <svg viewBox="0 0 240 200" className={className}>
    <g {...strokeProps}>
      {/* axes */}
      <path d="M30 170 L220 170" />
      <path d="M30 170 L30 20" />
      {/* rising line */}
      <path d="M40 155 Q80 140 100 120 T160 70 T210 30" />
      {/* dots */}
      <circle cx="60" cy="150" r="8" fill="#2A64F6" stroke="#0E0E0E" />
      <circle cx="100" cy="120" r="8" fill="#48B02C" stroke="#0E0E0E" />
      <circle cx="140" cy="90" r="8" fill="#FFB703" stroke="#0E0E0E" />
      <circle cx="180" cy="55" r="10" fill="#FF4D00" stroke="#0E0E0E" />
      {/* arrow tip */}
      <path d="M195 40 L210 30 L205 48" />
      {/* sparkles */}
      <path d="M45 40 L50 45 M45 45 L50 40" />
      <path d="M220 100 L225 105 M220 105 L225 100" />
    </g>
  </svg>
);

// Speech bubble
export const DoodleBubble = ({ className = "w-40 h-40 text-black" }) => (
  <svg viewBox="0 0 200 200" className={className}>
    <g {...strokeProps}>
      <path d="M30 40 Q30 30 40 30 L160 30 Q170 30 170 40 L170 130 Q170 140 160 140 L90 140 L60 170 L65 140 L40 140 Q30 140 30 130 Z" fill="#FDFBF7" />
      <circle cx="75" cy="85" r="5" fill="currentColor" stroke="none" />
      <circle cx="105" cy="85" r="5" fill="currentColor" stroke="none" />
      <path d="M75 105 Q100 120 125 105" />
    </g>
  </svg>
);

// Cube (rubik's-inspired)
export const DoodleCube = ({ className = "w-36 h-36 text-black" }) => (
  <svg viewBox="0 0 180 180" className={className}>
    <g {...strokeProps}>
      <rect x="30" y="30" width="60" height="60" fill="#FF4D00" />
      <rect x="90" y="30" width="60" height="60" fill="#FFB703" />
      <rect x="30" y="90" width="60" height="60" fill="#2A64F6" />
      <rect x="90" y="90" width="60" height="60" fill="#48B02C" />
      <path d="M45 30 L45 150 M60 30 L60 150 M75 30 L75 150 M105 30 L105 150 M120 30 L120 150 M135 30 L135 150" strokeWidth="1.5" opacity="0.35" />
      <path d="M30 45 L150 45 M30 60 L150 60 M30 75 L150 75 M30 105 L150 105 M30 120 L150 120 M30 135 L150 135" strokeWidth="1.5" opacity="0.35" />
    </g>
  </svg>
);

// Order-now ribbon
export const DoodleRibbon = ({ className = "w-52 h-16 text-black" }) => (
  <svg viewBox="0 0 260 80" className={className}>
    <g {...strokeProps}>
      <path d="M10 60 L20 20 L230 20 L250 40 L230 60 Z" fill="#FF4D00" />
      <text x="130" y="47" fontFamily="Montserrat, sans-serif" fontWeight="900" fontSize="20" fill="#FDFBF7" textAnchor="middle" stroke="none">
        ORDER NOW
      </text>
    </g>
  </svg>
);

// Sunglasses face (fun mascot for playful accents)
export const DoodleFace = ({ className = "w-32 h-32 text-black" }) => (
  <svg viewBox="0 0 160 160" className={className}>
    <g {...strokeProps}>
      <circle cx="80" cy="80" r="55" fill="#FFB703" />
      <rect x="30" y="65" width="45" height="20" rx="4" fill="#131313" stroke="none" />
      <rect x="85" y="65" width="45" height="20" rx="4" fill="#131313" stroke="none" />
      <path d="M75 75 L85 75" />
      <path d="M55 115 Q80 130 105 115" />
    </g>
  </svg>
);

// Arrow squiggle
export const DoodleArrow = ({ className = "w-24 h-16 text-black" }) => (
  <svg viewBox="0 0 200 100" className={className}>
    <g {...strokeProps}>
      <path d="M10 50 Q60 20 120 50 T190 50" />
      <path d="M170 30 L190 50 L170 70" />
    </g>
  </svg>
);
